import { Link } from "@tanstack/react-router";
import { Facebook, Instagram } from "lucide-react";

const socialLinks = [
  {
    name: "Instagram",
    href: "https://www.instagram.com/meitoolbox",
    icon: <Instagram className="h-4 w-4" />,
  },
  {
    name: "Facebook",
    href: "https://www.facebook.com/meitoolbox",
    icon: <Facebook className="h-4 w-4" />,
  },
  {
    name: "Pinterest",
    href: "https://br.pinterest.com/meitoolbox/",
    icon: (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
        <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345-.09.375-.293 1.194-.333 1.361-.052.219-.173.265-.4.16-1.494-.696-2.428-2.879-2.428-4.633 0-3.773 2.741-7.239 7.903-7.239 4.149 0 7.373 2.956 7.373 6.907 0 4.124-2.6 7.442-6.208 7.442-1.212 0-2.353-.63-2.744-1.374l-.746 2.846c-.27 1.04-1 2.342-1.489 3.135C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
      </svg>
    ),
  },
];

const footerTools = [
  { slug: "calculadora-das-mei", label: "Calculadora DAS" },
  { slug: "limite-faturamento-mei", label: "Limite de faturamento" },
  { slug: "calculadora-precificacao", label: "Precificação" },
  { slug: "valor-da-hora", label: "Valor da hora" },
  { slug: "margem-markup", label: "Margem & markup" },
  { slug: "validador-cnpj", label: "Validador de CNPJ" },
  { slug: "gerador-recibo", label: "Gerador de recibo" },
  { slug: "controle-entradas-saidas", label: "Controle financeiro" },
];

const footerDestaques = [
  { to: "/ferramentas/ia-mei" as const, label: "IA para tirar dúvidas do MEI" },
  { to: "/ferramentas/contratos-pdf" as const, label: "Gerador de contratos em PDF" },
];

const footerArticles = [
  { slug: "o-que-e-mei", label: "O que é MEI" },
  { slug: "como-formalizar-mei", label: "Como formalizar MEI" },
  { slug: "das-boleto-mei", label: "DAS / Boleto MEI" },
  { slug: "limite-faturamento-mei", label: "Limite de faturamento" },
  { slug: "mei-pode-ter-funcionario", label: "MEI pode ter funcionário?" },
  { slug: "como-cancelar-mei-dar-baixa", label: "Cancelar / dar baixa" },
];

export function SiteFooter() {
  return (
    <footer className="border-t border-border/60 bg-surface/50 mt-24">
      <div className="container mx-auto px-4 py-12 grid gap-10 md:grid-cols-5">
        <div className="md:col-span-2">
          <div className="font-display text-lg font-bold">MEI Toolbox</div>
          <p className="mt-2 text-sm text-muted-foreground max-w-xs">
            Ferramentas gratuitas, simples e diretas para microempreendedores brasileiros.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link
              to="/ferramentas"
              className="inline-flex items-center rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground hover:opacity-90 transition"
            >
              Ver ferramentas
            </Link>
            <Link
              to="/artigos"
              className="inline-flex items-center rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:border-primary/40 transition"
            >
              Ler artigos
            </Link>
          </div>
          <div className="mt-5">
            <div className="text-xs font-semibold text-muted-foreground mb-2">Siga-nos</div>
            <div className="flex gap-2">
              {socialLinks.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`MEI Toolbox no ${s.name}`}
                  title={s.name}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition"
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold mb-3">Ferramentas</h3>
          <ul className="space-y-1.5 text-sm text-muted-foreground">
            {footerTools.map((t) => (
              <li key={t.slug}>
                <Link
                  to="/ferramentas/$slug"
                  params={{ slug: t.slug }}
                  className="hover:text-foreground transition"
                >
                  {t.label}
                </Link>
              </li>
            ))}
            <li className="pt-1">
              <Link to="/ferramentas" className="text-primary font-semibold hover:underline">
                Ver todas →
              </Link>
            </li>
          </ul>
          <h3 className="text-sm font-semibold mt-5 mb-3">Em destaque</h3>
          <ul className="space-y-1.5 text-sm text-muted-foreground">
            {footerDestaques.map((d) => (
              <li key={d.to}>
                <Link to={d.to} className="hover:text-foreground transition">
                  {d.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-sm font-semibold mb-3">Artigos</h3>
          <ul className="space-y-1.5 text-sm text-muted-foreground">
            {footerArticles.map((a) => (
              <li key={a.slug}>
                <Link
                  to="/artigos/$slug"
                  params={{ slug: a.slug }}
                  className="hover:text-foreground transition"
                >
                  {a.label}
                </Link>
              </li>
            ))}
            <li className="pt-1">
              <Link to="/artigos" className="text-primary font-semibold hover:underline">
                Ver todos →
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="text-sm font-semibold mb-3">Site</h3>
          <ul className="space-y-1.5 text-sm text-muted-foreground">
            <li>
              <Link to="/" className="hover:text-foreground transition">
                Início
              </Link>
            </li>

            <li>
              <Link to="/contato" className="hover:text-foreground transition">
                Contato
              </Link>
            </li>
            <li>
              <Link to="/termos" className="hover:text-foreground transition">
                Termos e Condições
              </Link>
            </li>

            <li>
              <Link to="/privacidade" className="hover:text-foreground transition">
                Privacidade
              </Link>
            </li>
            <li>
              <Link to="/cookies" className="hover:text-foreground transition">
                Política de Cookies
              </Link>
            </li>
            <li>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent("mt:open-cookie-consent"))}
                className="hover:text-foreground transition text-left cursor-pointer border-none bg-transparent p-0"
              >
                Preferências de Cookies
              </button>
            </li>
            <li>
              <a href="/sitemap.xml" className="hover:text-foreground transition">
                Sitemap
              </a>
            </li>
            <li>
              <a href="/robots.txt" className="hover:text-foreground transition">
                robots.txt
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border/60 py-6 text-center text-xs text-muted-foreground px-4">
        © {new Date().getFullYear()} MEI Toolbox. Não somos órgão oficial. Sempre confira valores no
        Portal do Empreendedor.
      </div>
    </footer>
  );
}
